import React, { useCallback, useEffect, useState } from 'react'
import { api } from '../../lib/api'

export type GygSessionStatus = {
  api_session_ok: boolean | null
  playwright_session_ok: boolean | null
  needs_api_reconnection: boolean
  needs_playwright_refresh: boolean
  error_code?: string | null
  message?: string | null
}

export function gygStatusFromValidation(
  validation: Record<string, unknown> | null | undefined
): GygSessionStatus | null {
  if (!validation || typeof validation !== 'object') return null
  const apiOk = validation.authenticated === true && validation.session_ok === true
  const playwrightRaw = validation.playwright_session_ok
  const playwrightOk =
    playwrightRaw === true ? true : playwrightRaw === false ? false : null

  return {
    api_session_ok: apiOk,
    playwright_session_ok: playwrightOk,
    needs_api_reconnection: !apiOk,
    needs_playwright_refresh: playwrightOk === false,
    error_code: (validation.error_code as string | undefined) ?? null,
    message:
      (validation.troubleshooting_hint as string | undefined) ||
      (validation.error as string | undefined) ||
      null,
  }
}

function SessionLine({
  label,
  ok,
  needsAction,
  actionLabel,
}: {
  label: string
  ok: boolean | null
  needsAction: boolean
  actionLabel: string
}) {
  const tone =
    ok === true
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300'
      : needsAction
        ? 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200'
        : 'border-zinc-200 bg-zinc-50 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300'

  const statusLabel =
    ok === true ? 'Active' : needsAction ? actionLabel : 'Inconnu'

  return (
    <div className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-xs ${tone}`}>
      <span className="font-medium">{label}</span>
      <span>{statusLabel}</span>
    </div>
  )
}

interface GetYourGuideSessionStatusPanelProps {
  connectionId?: number | null
  validation?: Record<string, unknown> | null
  title?: string
  compact?: boolean
  className?: string
}

export default function GetYourGuideSessionStatusPanel({
  connectionId,
  validation,
  title = 'État des sessions GetYourGuide',
  compact = false,
  className = '',
}: GetYourGuideSessionStatusPanelProps) {
  const [status, setStatus] = useState<GygSessionStatus | null>(
    validation ? gygStatusFromValidation(validation) : null
  )
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  const loadStatus = useCallback(async () => {
    if (!connectionId) return
    setLoading(true)
    setFetchError(null)
    try {
      const res = await api.get(`/mcp/connections/${connectionId}/session-status`)
      setStatus({
        api_session_ok: res.data.api_session_ok ?? null,
        playwright_session_ok: res.data.playwright_session_ok ?? null,
        needs_api_reconnection: Boolean(res.data.needs_api_reconnection),
        needs_playwright_refresh: Boolean(res.data.needs_playwright_refresh),
        error_code: res.data.error_code ?? null,
        message: res.data.message ?? null,
      })
    } catch (err: any) {
      setFetchError(err.response?.data?.detail || 'Impossible de charger le statut')
    } finally {
      setLoading(false)
    }
  }, [connectionId])

  useEffect(() => {
    if (validation) {
      setStatus(gygStatusFromValidation(validation))
    }
  }, [validation])

  useEffect(() => {
    if (connectionId && !validation) {
      loadStatus()
    }
  }, [connectionId, validation, loadStatus])

  const derived = validation ? gygStatusFromValidation(validation) : status
  const showPanel = Boolean(connectionId || validation)

  if (!showPanel) return null

  return (
    <div
      className={`rounded-xl border border-emerald-100/80 bg-emerald-50/40 p-4 dark:border-emerald-900/40 dark:bg-emerald-950/20 ${className}`}
    >
      <div className={`flex items-start justify-between gap-3 ${compact ? 'mb-2' : 'mb-3'}`}>
        <div>
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">{title}</p>
          {!compact && (
            <p className="mt-1 text-xs text-emerald-800/80 dark:text-emerald-300/80">
              Session API (compte) et session navigateur (agenda / réservations).
            </p>
          )}
        </div>
        {connectionId && (
          <button
            type="button"
            onClick={loadStatus}
            disabled={loading}
            className="shrink-0 rounded-full border border-emerald-200 bg-white px-3 py-1 text-xs font-medium text-emerald-800 transition hover:bg-emerald-50 disabled:opacity-50 dark:border-emerald-800 dark:bg-zinc-900 dark:text-emerald-200 dark:hover:bg-zinc-800"
          >
            {loading ? '…' : 'Actualiser'}
          </button>
        )}
      </div>

      {fetchError && (
        <p className="mb-2 text-xs text-red-600 dark:text-red-400">{fetchError}</p>
      )}

      {loading && !derived ? (
        <p className="text-xs text-zinc-500 dark:text-zinc-400">Vérification en cours…</p>
      ) : derived ? (
        <div className="space-y-2">
          <SessionLine
            label="Session API"
            ok={derived.api_session_ok}
            needsAction={derived.needs_api_reconnection}
            actionLabel="Reconnexion requise"
          />
          <SessionLine
            label="Session navigateur (Playwright)"
            ok={derived.playwright_session_ok}
            needsAction={derived.needs_playwright_refresh}
            actionLabel="Code MFA requis"
          />
          {derived.message && (derived.needs_api_reconnection || derived.needs_playwright_refresh) && (
            <p className="text-xs text-amber-800 dark:text-amber-200">{derived.message}</p>
          )}
        </div>
      ) : null}
    </div>
  )
}
